import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const ticketErrorTypeValues = [
  "UNKNOWN",
  "TICKET_TEAM_INVALID",
  "BID_NOT_FOUND",
  "TICKET_NOT_FOUND",
  "PROJECT_MISSING_ESTIMATED_START_DATE_OR_BID",
  "TICKET_ALREADY_RESOLVED",
  "TICKET_INVALID_TRANSITION",
  "TICKET_DEDUP_RACE",
  "ACTIVE_AUTOMATED_TICKET_ALREADY_EXISTS",
  "TICKET_MOVE_INDEX_MISALIGNED",
  "TICKET_SUPPRESSED_UPDATE_REQUIRES_REASON",
  "TICKET_STALE_QUERY_TIMEOUT",
  "TICKET_STALE_CURSOR_INVALID",
] as const;
const ticketErrorTypeSchema = z.enum(ticketErrorTypeValues);
const TicketErrorType = ticketErrorTypeSchema.enum;
type TicketErrorType = z.infer<typeof ticketErrorTypeSchema>;

export type TicketErrors =
  | { errorType: typeof TicketErrorType.UNKNOWN; data: undefined }
  | { errorType: typeof TicketErrorType.TICKET_TEAM_INVALID; data: { teamId: string } }
  | { errorType: typeof TicketErrorType.BID_NOT_FOUND; data: { bidId: number } }
  | { errorType: typeof TicketErrorType.TICKET_NOT_FOUND; data: { id: string } }
  | { errorType: typeof TicketErrorType.PROJECT_MISSING_ESTIMATED_START_DATE_OR_BID; data: { projectId: number } }
  | { errorType: typeof TicketErrorType.TICKET_ALREADY_RESOLVED; data: { ticketId: string } }
  | {
      errorType: typeof TicketErrorType.TICKET_INVALID_TRANSITION;
      data: { ticketId: string; currentStatus: string; targetStatus: string };
    }
  | {
      errorType: typeof TicketErrorType.TICKET_DEDUP_RACE;
      data: { bidId: number; automatedTicketType: string | null };
    }
  | {
      errorType: typeof TicketErrorType.ACTIVE_AUTOMATED_TICKET_ALREADY_EXISTS;
      data: {
        ticketId: string;
        holderTicketId: string | null;
        holderStatus: string | null;
        bidId: number;
        automatedTicketType: string | null;
      };
    }
  | {
      errorType: typeof TicketErrorType.TICKET_MOVE_INDEX_MISALIGNED;
      data: { workSiteId: number; changeGroupId: string; ticketMoveBatchIndex: number };
    }
  | { errorType: typeof TicketErrorType.TICKET_SUPPRESSED_UPDATE_REQUIRES_REASON; data: { ticketId: string } }
  | {
      errorType: typeof TicketErrorType.TICKET_STALE_QUERY_TIMEOUT;
      data: { staleForDays: number; take: number; timeoutCode: string };
    }
  | { errorType: typeof TicketErrorType.TICKET_STALE_CURSOR_INVALID; data: undefined };

export const ticketErrorDetails: Record<TicketErrorType, ErrorDetails> = {
  UNKNOWN: { classification: ErrorClassification.UNEXPECTED_ERROR, sentryDescription: "Unknown Error" },
  TICKET_TEAM_INVALID: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Ticket team invalid or not assignable",
  },
  BID_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Bid not found" },
  TICKET_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Ticket not found",
  },
  PROJECT_MISSING_ESTIMATED_START_DATE_OR_BID: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Project is missing estimated start date or bid",
  },
  TICKET_ALREADY_RESOLVED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Ticket is already resolved",
  },
  TICKET_INVALID_TRANSITION: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Invalid ticket status transition",
  },
  TICKET_DEDUP_RACE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Automated ticket dedup race could not recover the committed ticket",
  },
  ACTIVE_AUTOMATED_TICKET_ALREADY_EXISTS: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "An active automated ticket already exists for this tuple",
  },
  TICKET_MOVE_INDEX_MISALIGNED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Ticket move batch results misaligned with the stranded-candidate index",
  },
  TICKET_SUPPRESSED_UPDATE_REQUIRES_REASON: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Suppressed ticket update supplied no reason",
  },
  TICKET_STALE_QUERY_TIMEOUT: {
    classification: ErrorClassification.TIMEOUT_ERROR,
    sentryDescription: "Stale-ticket queue query exceeded its MAX_EXECUTION_TIME ceiling",
  },
  TICKET_STALE_CURSOR_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Stale-ticket cursor was malformed, stale, or issued under a different sort",
  },
};
